import { ref, computed } from 'vue';

// Global state for sales data
const bills = ref([]);

export const useSalesAggregation = () => {
  // Exchange rates (simplified)
  const exchangeRates = {
    'USD': 35,
    'EUR': 38,
    'JPY': 0.25,
    'THB': 1
  };

  // Convert amount to THB
  const convertToTHB = (amount, currency = 'THB') => {
    // Handle undefined, null, or invalid amounts
    if (amount === undefined || amount === null || amount === '') {
      return 0;
    }
    
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) {
      return 0;
    }
    
    const rate = exchangeRates[currency] || 1;
    return numericAmount * rate;
  };

  // Aggregate sales by cluster
  const salesByCluster = computed(() => {
    const aggregated = {};
    
    bills.value.forEach(bill => {
      // Safety check for bill object
      if (!bill || typeof bill !== 'object') return;
      
      const clusterId = bill.clusterId;
      if (!clusterId) return;
      
      if (!aggregated[clusterId]) {
        aggregated[clusterId] = {
          totalInTHB: 0,
          billCount: 0,
          byCurrency: {},
          bills: [],
          omSales: {}
        };
      }
      
      const amountInTHB = convertToTHB(bill.amount, bill.currency);
      aggregated[clusterId].totalInTHB += amountInTHB;
      aggregated[clusterId].billCount++;
      aggregated[clusterId].bills.push(bill);
      
      // Track by currency
      const currency = bill.currency || 'THB';
      aggregated[clusterId].byCurrency[currency] = 
        (aggregated[clusterId].byCurrency[currency] || 0) + parseFloat(bill.amount);
      
      // สร้าง nested OM structure
      const omId = bill.omId;
      if (omId) {
        if (!aggregated[clusterId].omSales[omId]) {
          aggregated[clusterId].omSales[omId] = {
            totalInTHB: 0,
            billCount: 0,
            byCurrency: {},
            bills: [],
            vcSales: {}
          };
        }
        
        aggregated[clusterId].omSales[omId].totalInTHB += amountInTHB;
        aggregated[clusterId].omSales[omId].billCount++;
        aggregated[clusterId].omSales[omId].bills.push(bill);
        aggregated[clusterId].omSales[omId].byCurrency[currency] = 
          (aggregated[clusterId].omSales[omId].byCurrency[currency] || 0) + parseFloat(bill.amount);
        
        // สร้าง nested VC structure
        const vcId = bill.vcId;
        if (vcId) {
          if (!aggregated[clusterId].omSales[omId].vcSales[vcId]) {
            aggregated[clusterId].omSales[omId].vcSales[vcId] = {
              totalInTHB: 0,
              billCount: 0,
              byCurrency: {},
              bills: []
            };
          }
          
          aggregated[clusterId].omSales[omId].vcSales[vcId].totalInTHB += amountInTHB;
          aggregated[clusterId].omSales[omId].vcSales[vcId].billCount++;
          aggregated[clusterId].omSales[omId].vcSales[vcId].bills.push(bill);
          aggregated[clusterId].omSales[omId].vcSales[vcId].byCurrency[currency] = 
            (aggregated[clusterId].omSales[omId].vcSales[vcId].byCurrency[currency] || 0) + parseFloat(bill.amount);
        }
      }
    });
    
    return aggregated;
  });

  // Aggregate sales by OM
  const salesByOM = computed(() => {
    const aggregated = {};
    
    bills.value.forEach(bill => {
      // Safety check for bill object
      if (!bill || typeof bill !== 'object') return;
      
      const omId = bill.omId;
      if (!omId) return;
      
      if (!aggregated[omId]) {
        aggregated[omId] = {
          totalInTHB: 0,
          billCount: 0,
          byCurrency: {},
          bills: [],
          parentClusterId: bill.clusterId
        };
      }
      
      const amountInTHB = convertToTHB(bill.amount, bill.currency);
      aggregated[omId].totalInTHB += amountInTHB;
      aggregated[omId].billCount++;
      aggregated[omId].bills.push(bill);
      
      // Track by currency
      const currency = bill.currency || 'THB';
      aggregated[omId].byCurrency[currency] = 
        (aggregated[omId].byCurrency[currency] || 0) + parseFloat(bill.amount);
    });
    
    return aggregated;
  });

  // Aggregate sales by VC
  const salesByVC = computed(() => {
    const aggregated = {};
    
    bills.value.forEach(bill => {
      // Safety check for bill object
      if (!bill || typeof bill !== 'object') return;
      
      const vcId = bill.vcId;
      if (!vcId) return;
      
      if (!aggregated[vcId]) {
        aggregated[vcId] = {
          totalInTHB: 0,
          billCount: 0,
          byCurrency: {},
          bills: [],
          parentOmId: bill.omId,
          parentClusterId: bill.clusterId
        };
      }
      
      const amountInTHB = convertToTHB(bill.amount, bill.currency);
      aggregated[vcId].totalInTHB += amountInTHB;
      aggregated[vcId].billCount++;
      aggregated[vcId].bills.push(bill);
      
      // Track by currency
      const currency = bill.currency || 'THB';
      aggregated[vcId].byCurrency[currency] = 
        (aggregated[vcId].byCurrency[currency] || 0) + parseFloat(bill.amount);
    });
    
    return aggregated;
  });

  // Aggregate sales by Sub-VC
  const salesBySubVC = computed(() => {
    const aggregated = {};
    
    bills.value.forEach(bill => {
      // Safety check for bill object
      if (!bill || typeof bill !== 'object') return;
      
      const subVcId = bill.subVcId;
      if (!subVcId) return;
      
      if (!aggregated[subVcId]) {
        aggregated[subVcId] = {
          totalInTHB: 0,
          billCount: 0,
          byCurrency: {},
          bills: [],
          parentVcId: bill.vcId,
          parentOmId: bill.omId,
          parentClusterId: bill.clusterId
        };
      }
      
      const amountInTHB = convertToTHB(bill.amount, bill.currency);
      aggregated[subVcId].totalInTHB += amountInTHB;
      aggregated[subVcId].billCount++;
      aggregated[subVcId].bills.push(bill);
      
      // Track by currency
      const currency = bill.currency || 'THB';
      aggregated[subVcId].byCurrency[currency] = 
        (aggregated[subVcId].byCurrency[currency] || 0) + parseFloat(bill.amount);
    });
    
    return aggregated;
  });

  // Get total sales in THB
  const getTotalSalesInTHB = () => {
    return bills.value.reduce((total, bill) => {
      // Check if bill and bill.amount exist
      if (!bill || bill.amount === undefined || bill.amount === null) {
        return total;
      }
      return total + convertToTHB(bill.amount, bill.currency);
    }, 0);
  };

  // Format currency
  const formatCurrency = (amount, currency = 'THB') => {
    try {
      const numericAmount = parseFloat(amount);
      if (isNaN(numericAmount)) return '฿0';
      
      if (currency === 'THB') {
        // แสดงยอดเงินจริงพร้อม comma ไม่ย่อ
        return `฿${numericAmount.toLocaleString('th-TH', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })}`;
      } else {
        // For other currencies, show with currency code
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(numericAmount);
      }
    } catch (e) {
      console.error('Error formatting currency:', e);
      return '฿0';
    }
  };

  // Set bills data
  const setBills = (newBills) => {
    bills.value = newBills || [];
  };

  return {
    bills,
    setBills,
    salesByCluster,
    salesByOM,
    salesByVC,
    salesBySubVC,
    getTotalSalesInTHB,
    formatCurrency,
    convertToTHB
  };
}; 